// Admin card (Settings → Integrations): enable/configure the Benefits module per
// tenant. CRITICAL: Allowances and Deductions toggle independently of the full
// module — a company can run payroll allowances/deductions with Benefits off.
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { Switch } from '@/features/shared/components/ui/switch';
import { HeartPulse, Loader2 } from 'lucide-react';
import { getBenefitsSettings, updateBenefitsSettings, type BenefitsSettings } from '@/lib/hrApi';

type Key = keyof BenefitsSettings;

// label/desc resolve to benefits:set_<key> / set_<key>_desc translation keys.
const ROWS: Array<{ key: Key; group: 'module' | 'independent' }> = [
  { key: 'benefits_enabled', group: 'module' },
  { key: 'insurance_enabled', group: 'module' },
  { key: 'retirement_enabled', group: 'module' },
  { key: 'self_enrollment_enabled', group: 'module' },
  { key: 'hr_approval_required', group: 'module' },
  { key: 'show_employee_view', group: 'module' },
  { key: 'allowances_enabled', group: 'independent' },
  { key: 'deductions_enabled', group: 'independent' },
];

export const BenefitsSettingsCard: React.FC = () => {
  const { t } = useTranslation('benefits');
  const [s, setS] = useState<BenefitsSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);

  useEffect(() => { getBenefitsSettings().then(setS).catch(() => setS(null)).finally(() => setLoading(false)); }, []);

  const toggle = async (key: Key, value: boolean) => {
    if (!s) return;
    setSaving(key);
    setS({ ...s, [key]: value }); // optimistic
    try { setS(await updateBenefitsSettings({ [key]: value })); }
    catch { setS({ ...s, [key]: !value }); } // revert
    finally { setSaving(null); }
  };

  const Row: React.FC<{ k: Key }> = ({ k }) => {
    const label = t(`set_${k}`);
    return (
      <div className="flex items-center justify-between rounded-lg border border-border p-3">
        <div className="min-w-0 pr-3">
          <p className="text-sm font-medium text-foreground">{label}</p>
          <p className="text-xs text-muted-foreground">{t(`set_${k}_desc`)}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {saving === k ? <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /> : null}
          <Switch checked={!!s?.[k]} onCheckedChange={(v) => toggle(k, v)} disabled={!s || saving === k} aria-label={label} />
        </div>
      </div>
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><HeartPulse className="w-5 h-5 text-orange-600" /> {t('title')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> {t('loading')}</div>
        ) : (
          <>
            <div className="space-y-2">
              {ROWS.filter((r) => r.group === 'module').map((r) => <Row key={r.key} k={r.key} />)}
            </div>
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">{t('settings_payroll_items')}</p>
              <div className="space-y-2">
                {ROWS.filter((r) => r.group === 'independent').map((r) => <Row key={r.key} k={r.key} />)}
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default BenefitsSettingsCard;
